/**
 * ReservePayoutDialog Component
 * Pay out some or all of a Finalised contract's remaining (un-rolled) reserve.
 *
 * At finalisation each investor's net value is split into Rollover Suspense
 * (reserve, held for future rollover) and Payout. Reserved funds are drawn down
 * when a new contract rolls from them. This dialog releases the residual reserve
 * that was never rolled: per investor, pay out any amount up to the remaining
 * (reserve - already rolled - already paid out). Always submitted for approval.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Grid,
  Typography,
  Box,
  Paper,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  ButtonGroup,
} from '@mui/material';
import { format } from 'date-fns';
import { apiPost, apiGet } from '../../utils/apiClient';
import ContractActionDialogBase from './ContractActionDialogBase';

// Format number with space thousand separator and period decimal
const formatWithSpaces = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0.00';
  return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

// Currency input: formatted when blurred, raw number when focused
function CurrencyInput({ value, onChange, label, helperText, disabled, sx, ...props }) {
  const [focused, setFocused] = useState(false);
  const showFormatted = !focused || disabled;
  const rawValue = (() => {
    const num = parseFloat(value);
    return isNaN(num) ? '' : String(num);
  })();

  return (
    <TextField
      {...props}
      label={label}
      fullWidth
      value={showFormatted ? formatWithSpaces(value) : rawValue}
      onChange={disabled ? undefined : (e) => onChange(e.target.value.replace(/\s/g, ''))}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      type={showFormatted ? 'text' : 'number'}
      inputProps={showFormatted ? {} : { step: 0.01 }}
      InputLabelProps={{ shrink: true }}
      helperText={helperText}
      disabled={disabled}
      sx={sx}
    />
  );
}

const ReservePayoutDialog = ({ open, onClose, contract, contractId: explicitContractId, onSubmitSuccess }) => {
  const contractId = explicitContractId || contract?.id;

  const [payoutDate, setPayoutDate] = useState('');
  const [reason, setReason] = useState('');
  const [investorRows, setInvestorRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset state and default the payout date to today when the dialog opens.
  useEffect(() => {
    if (open) {
      setPayoutDate(format(new Date(), 'yyyy-MM-dd'));
      setReason('');
      setInvestorRows([]);
      setError(null);
    }
  }, [open]);

  // Fetch per-investor remaining reserve.
  const fetchSummary = useCallback(async () => {
    if (!contractId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiGet(`/contracts/${contractId}/reserve-summary`);
      const rows = (result.data || [])
        .map((inv) => ({
          investor_id: inv.investor_id,
          investor_name: inv.investor_name,
          reserve_amount: parseFloat(inv.reserve_amount) || 0,
          already_rolled: parseFloat(inv.already_rolled) || 0,
          paid_out_amount: parseFloat(inv.paid_out_amount) || 0,
          remaining: parseFloat(inv.remaining) || 0,
          payout_amount: 0, // default: nothing selected
        }))
        // Only investors with a positive remaining reserve are payable.
        .filter((r) => r.remaining > 0.005);
      setInvestorRows(rows);
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to load reserve summary');
      setInvestorRows([]);
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    if (open) {
      fetchSummary();
    }
  }, [open, fetchSummary]);

  // Handle payout amount change for an investor (clamped 0..remaining).
  const handlePayoutChange = (index, value) => {
    const num = parseFloat(value) || 0;
    setInvestorRows((prev) => {
      const updated = [...prev];
      const row = { ...updated[index] };
      row.payout_amount = Math.max(0, Math.min(num, row.remaining));
      updated[index] = row;
      return updated;
    });
  };

  // Quick-action: pay out all remaining
  const setAllRemaining = () => {
    setInvestorRows((prev) => prev.map((row) => ({ ...row, payout_amount: row.remaining })));
  };

  // Quick-action: clear all
  const clearAll = () => {
    setInvestorRows((prev) => prev.map((row) => ({ ...row, payout_amount: 0 })));
  };

  // Validation: at least one positive payout, none over remaining.
  const totalPayout = investorRows.reduce((s, r) => s + (parseFloat(r.payout_amount) || 0), 0);
  const allocationValid = investorRows.length > 0
    && totalPayout > 0
    && investorRows.every((row) => (parseFloat(row.payout_amount) || 0) <= row.remaining + 0.005);
  const isValid = !!payoutDate && allocationValid && !!reason;

  // Totals
  const totalReserve = investorRows.reduce((s, r) => s + r.reserve_amount, 0);
  const totalRolled = investorRows.reduce((s, r) => s + r.already_rolled, 0);
  const totalAlreadyPaid = investorRows.reduce((s, r) => s + r.paid_out_amount, 0);
  const totalRemaining = investorRows.reduce((s, r) => s + r.remaining, 0);

  // Submit
  const handleSubmit = async ({ proof_document_ids }) => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        payout_date: payoutDate,
        investor_allocations: investorRows
          .filter((row) => (parseFloat(row.payout_amount) || 0) > 0)
          .map((row) => ({
            investor_id: row.investor_id,
            payout_amount: parseFloat(row.payout_amount) || 0,
          })),
        reason,
        proof_document_ids,
      };
      const response = await apiPost(`/contracts/${contractId}/payout-reserve`, payload);
      onClose();
      if (onSubmitSuccess) onSubmitSuccess(response.data?.status || 'pending');
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to submit reserve payout');
    } finally {
      setSubmitting(false);
    }
  };

  const currencyCode = contract?.currency_code || investorRows[0]?.currency_code || 'ZAR';

  return (
    <ContractActionDialogBase
      open={open}
      onClose={onClose}
      contract={contract}
      workflowCode="contract_finalise"
      title="Pay Out Reserve"
      description="Release the remaining (un-rolled) reserve for one or more investors on this Finalised contract. Enter the amount to pay out per investor, up to each investor's remaining reserve. This will be submitted for approval; the payout is matched later on the Payment Matching screen."
      submitLabel="Submit for Approval"
      submitColor="primary"
      isValid={isValid}
      submitting={submitting}
      error={error}
      onClearError={() => setError(null)}
      onSubmit={handleSubmit}
      reason={reason}
      onReasonChange={setReason}
      maxWidth="lg"
    >
      {/* Payout Date */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            label="Payout Date"
            type="date"
            fullWidth
            value={payoutDate}
            onChange={(e) => setPayoutDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="textSecondary">Loading reserve summary...</Typography>
        </Box>
      )}

      {/* Reserve table */}
      {!loading && investorRows.length > 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">Per-Investor Remaining Reserve</Typography>
            <ButtonGroup size="small" variant="outlined">
              <Button onClick={setAllRemaining}>Pay Out All Remaining</Button>
              <Button onClick={clearAll}>Clear</Button>
            </ButtonGroup>
          </Box>

          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>Investor</TableCell>
                  <TableCell align="right">Reserve</TableCell>
                  <TableCell align="right">Already Rolled</TableCell>
                  <TableCell align="right">Already Paid Out</TableCell>
                  <TableCell align="right">Remaining</TableCell>
                  <TableCell align="right" sx={{ minWidth: 160 }}>Pay Out Now</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {investorRows.map((row, idx) => (
                  <TableRow key={row.investor_id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{row.investor_name}</Typography>
                    </TableCell>
                    <TableCell align="right">{formatWithSpaces(row.reserve_amount)}</TableCell>
                    <TableCell align="right">{formatWithSpaces(row.already_rolled)}</TableCell>
                    <TableCell align="right">{formatWithSpaces(row.paid_out_amount)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">{formatWithSpaces(row.remaining)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <CurrencyInput
                        size="small"
                        value={row.payout_amount}
                        onChange={(v) => handlePayoutChange(idx, v)}
                        sx={{ '& input': { textAlign: 'right' } }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals row */}
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><Typography variant="body2" fontWeight="bold">Total</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold">{formatWithSpaces(totalReserve)}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold">{formatWithSpaces(totalRolled)}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold">{formatWithSpaces(totalAlreadyPaid)}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold">{formatWithSpaces(totalRemaining)}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight="bold" color="warning.main">{formatWithSpaces(totalPayout)}</Typography></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Summary chip */}
          <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', gap: 3, justifyContent: 'flex-end', mb: 1 }}>
            <Box>
              <Typography variant="caption" color="textSecondary">Total Payout</Typography>
              <Typography variant="body2" fontWeight="bold" color="warning.main">
                {currencyCode} {formatWithSpaces(totalPayout)}
              </Typography>
            </Box>
          </Paper>
        </>
      )}

      {/* No remaining reserve */}
      {!loading && investorRows.length === 0 && !error && (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          This contract has no remaining reserve to pay out. All reserved funds have been rolled or already paid out.
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />
    </ContractActionDialogBase>
  );
};

export default ReservePayoutDialog;
